import { createDefaultParameterConfig } from './sweetSpotDefaults';
import type { EvaluationMethodConfig, WeightStrategy } from './sweetSpotEvaluationEngine';
import { createParameterConfigFromTemplate, getReservoirTemplate } from './sweetSpotReservoir';
import type { ParameterGroup, ReservoirType, SweetSpotParameterConfig } from './types';

export const SSI_METHOD_KEY = 'legacy-rule@1.0.0' as const;

export const WEIGHT_SCHEME_LABELS: Record<WeightStrategy, string> = {
  fixed: '预设权重',
  user: '自定义权重',
  ahp: '层次分析（AHP）',
};

const GROUPS: ParameterGroup[] = ['rq', 'cq'];

export function captureWeightMap(parameters: SweetSpotParameterConfig): Record<string, number> {
  return Object.fromEntries(GROUPS.flatMap((group) => parameters[group].map((parameter) => [
    parameter.id,
    Number.isFinite(parameter.weight) && parameter.weight >= 0 ? parameter.weight : 0,
  ])));
}

function applyWeightMap(
  parameters: SweetSpotParameterConfig,
  weights: Record<string, number>,
): SweetSpotParameterConfig {
  return {
    rq: parameters.rq.map((parameter) => ({
      ...parameter,
      weight: weights[parameter.id] ?? parameter.weight,
    })),
    cq: parameters.cq.map((parameter) => ({
      ...parameter,
      weight: weights[parameter.id] ?? parameter.weight,
    })),
  };
}

/**
 * 依据当前指标集合选择系统预设权重。经典 SSI 指标使用既有工程权重；
 * 储层模板指标使用模板权重，未收录的自定义指标在所属组内等权处理。
 */
export function buildRecommendedParameters(
  current: SweetSpotParameterConfig,
  reservoirType: ReservoirType,
): SweetSpotParameterConfig {
  const classic = createDefaultParameterConfig();
  const template = createParameterConfigFromTemplate(getReservoirTemplate(reservoirType));
  const currentIds = new Set(GROUPS.flatMap((group) => current[group].map((item) => item.id)));
  const coverage = (candidate: SweetSpotParameterConfig) => GROUPS
    .flatMap((group) => candidate[group])
    .filter((item) => currentIds.has(item.id)).length;
  const preset = coverage(template) > coverage(classic) ? template : classic;

  return {
    rq: applyRecommendedGroup(current.rq, preset.rq),
    cq: applyRecommendedGroup(current.cq, preset.cq),
  };
}

function applyRecommendedGroup(
  current: SweetSpotParameterConfig[ParameterGroup],
  preset: SweetSpotParameterConfig[ParameterGroup],
) {
  const enabledCount = current.filter((item) => item.enabled).length;
  return current.map((parameter) => ({
    ...parameter,
    weight: preset.find((item) => item.id === parameter.id)?.weight
      ?? (parameter.enabled && enabledCount > 0 ? 1 / enabledCount : 0),
  }));
}

export function switchWeightScheme(
  config: EvaluationMethodConfig,
  next: WeightStrategy,
  recommended: SweetSpotParameterConfig,
): EvaluationMethodConfig {
  if (next === config.weightStrategy) return config;
  const customWeights = config.weightStrategy === 'user'
    ? captureWeightMap(config.parameters)
    : config.customWeights;
  const parameters = next === 'fixed'
    ? recommended
    : next === 'user' && customWeights
      ? applyWeightMap(config.parameters, customWeights)
      : config.parameters;
  return {
    ...config,
    parameters,
    customWeights,
    weightStrategy: next,
    reviewState: undefined,
  };
}
